import React from 'react';
import {
  Globe,
  Video,
  CreditCard,
  UserCheck,
  Phone,
  CircleHelp,
  ExternalLink,
} from 'lucide-react';
import QRCode from 'react-qr-code';
import { campaignConfig } from '../config/customerConfig';

// Legacy brand palette for this volunteer-facing panel. Kept as inline styles so
// the migration renders identically; a future dark-mode pass can tokenize these.
const PURPLE = '#2f1c6a';
const MUTED = '#999';
const TIP = '#5a4080';

interface VolunteerResourcesSectionProps {
  showQuickTips?: boolean;
  showQRCode?: boolean;
}

const VolunteerResourcesSection: React.FC<VolunteerResourcesSectionProps> = ({
  showQuickTips = true,
  showQRCode = true,
}) => {
  return (
    <div>
      {/* Campaign Information */}
      <div className="mb-6">
        <h3 className="mb-1 text-sm font-semibold" style={{ color: PURPLE }}>
          Campaign Information
        </h3>
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1">
            <Globe className="h-5 w-5 shrink-0" style={{ color: PURPLE }} />
            {campaignConfig.campaignWebsite ? (
              <a
                href={campaignConfig.campaignWebsite}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm no-underline"
                style={{ color: PURPLE }}
              >
                Campaign Website{' '}
                <ExternalLink className="ml-0.5 inline-block h-3.5 w-3.5 align-middle" />
              </a>
            ) : (
              <span className="text-sm italic" style={{ color: MUTED }}>
                [REACT_APP_CAMPAIGN_WEBSITE]
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            <Video className="h-5 w-5 shrink-0" style={{ color: PURPLE }} />
            {campaignConfig.campaignYoutube ? (
              <a
                href={campaignConfig.campaignYoutube}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm no-underline"
                style={{ color: PURPLE }}
              >
                Campaign Videos{' '}
                <ExternalLink className="ml-0.5 inline-block h-3.5 w-3.5 align-middle" />
              </a>
            ) : (
              <span className="text-sm italic" style={{ color: MUTED }}>
                [REACT_APP_CAMPAIGN_YOUTUBE]
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Support the Campaign */}
      <div className="mb-6">
        <h3 className="mb-1 text-sm font-semibold" style={{ color: PURPLE }}>
          Support the Campaign
        </h3>
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-1">
            <CreditCard className="h-5 w-5 shrink-0" style={{ color: PURPLE }} />
            {campaignConfig.campaignVenmo ? (
              <>
                <span className="text-sm" style={{ color: PURPLE }}>
                  Venmo: {campaignConfig.campaignVenmo}
                </span>
                {showQRCode && (
                  <div className="ml-1 rounded border border-gray-300 bg-white p-1">
                    <QRCode
                      value={`https://venmo.com/${campaignConfig.campaignVenmo.replace('@', '')}`}
                      size={80}
                      style={{ height: 'auto', maxWidth: '100%', width: '100%' }}
                    />
                  </div>
                )}
              </>
            ) : (
              <span className="text-sm italic" style={{ color: MUTED }}>
                [REACT_APP_CAMPAIGN_VENMO]
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Voter Resources */}
      <div className="mb-6">
        <h3 className="mb-1 text-sm font-semibold" style={{ color: PURPLE }}>
          Voter Resources
        </h3>
        <div className="flex items-center gap-1">
          <UserCheck className="h-5 w-5 shrink-0" style={{ color: PURPLE }} />
          {campaignConfig.voterRegistrationUrl ? (
            <a
              href={campaignConfig.voterRegistrationUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm no-underline"
              style={{ color: PURPLE }}
            >
              Check Voter Registration{' '}
              <ExternalLink className="ml-0.5 inline-block h-3.5 w-3.5 align-middle" />
            </a>
          ) : (
            <span className="text-sm italic" style={{ color: MUTED }}>
              [REACT_APP_VOTER_REGISTRATION_URL]
            </span>
          )}
        </div>
      </div>

      {/* Support & Help */}
      <div className="mb-6">
        <h3 className="mb-1 text-sm font-semibold" style={{ color: PURPLE }}>
          Support & Help
        </h3>
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1">
            <Phone className="h-5 w-5 shrink-0" style={{ color: PURPLE }} />
            <span
              className="text-sm"
              style={{
                color: campaignConfig.volunteerHotline ? PURPLE : MUTED,
                fontStyle: campaignConfig.volunteerHotline ? 'normal' : 'italic',
              }}
            >
              Volunteer Hotline: {campaignConfig.volunteerHotline || '[REACT_APP_VOLUNTEER_HOTLINE]'}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <CircleHelp className="h-5 w-5 shrink-0" style={{ color: PURPLE }} />
            <span
              className="text-sm"
              style={{
                color: campaignConfig.supportEmail ? PURPLE : MUTED,
                fontStyle: campaignConfig.supportEmail ? 'normal' : 'italic',
              }}
            >
              App Support: Email {campaignConfig.supportEmail || '[REACT_APP_SUPPORT_EMAIL]'}
            </span>
          </div>
        </div>
      </div>

      {/* Quick Tips */}
      {showQuickTips && (
        <div
          className="rounded-lg p-4"
          style={{
            background: 'rgba(47, 28, 106, 0.05)',
            border: '1px solid rgba(47, 28, 106, 0.1)',
          }}
        >
          <h3 className="mb-1 text-sm font-semibold" style={{ color: PURPLE }}>
            Canvassing Quick Tips
          </h3>
          <div className="flex flex-col gap-0.5 text-sm" style={{ color: TIP }}>
            <span>• Always wear your volunteer badge</span>
            <span>• Be respectful and polite</span>
            <span>• Don't argue with voters</span>
            <span>• Use the app to log all contacts</span>
            <span>• Ask for help if you need it</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default VolunteerResourcesSection;
